// How an order's payment reads in one line, on the till's Orders screen and
// on the owner's (UC-6, UC-16, UC-35).

import { formatMinor } from './money';
import { fill, isArabic, message, type Language, type MessageKey } from '../i18n/messages';
import type { components } from '../api/types';

type Order = Pick<components['schemas']['OrderBody'], 'status' | 'totalMinor' | 'payment'>;

type Numbered = Pick<components['schemas']['OrderBody'], 'number' | 'prefix' | 'tillNumber'>;

/**
 * The number a list shows: the till's prefix-N for an order a till sent on
 * the hot track (UC-40), such as A-12, and the shop's number for every other
 * order. The server's receipt reads the same (domain.Order.DisplayNumber).
 */
export function orderNumber(order: Numbered): string {
	return order.prefix && order.tillNumber
		? tillOrderNumber(order.prefix, order.tillNumber)
		: `${order.number}`;
}

/**
 * A trading day, YYYY-MM-DD, as a till names an open order's older day
 * (#348): the day and the month, "12 Oct", with Latin digits in every
 * language. Read as the date it names, so no time zone moves it.
 */
export function tradingDayLabel(day: string, language: Language): string {
	const [year, month, date] = day.split('-').map(Number);
	if (!year || !month || !date) return day;
	const parts = new Intl.DateTimeFormat(isArabic(language) ? 'ar-EG-u-nu-latn' : 'en-US', {
		day: 'numeric',
		month: isArabic(language) ? 'long' : 'short',
	}).formatToParts(new Date(year, month - 1, date));
	const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
	return `${part('day')} ${part('month')}`;
}

/**
 * The till's own number on the order: its prefix and its number on the
 * trading day, such as A-12 (#371). An order's `number` is the till's own in
 * the order store's answers; a prefix-less one reads as the number alone.
 */
export function tillOrderNumber(prefix: string | undefined, number: number): string {
	return prefix ? `${prefix}-${number}` : `${number}`;
}

/**
 * Why the server did not take a sale the till's order store sent (#371), by
 * the hot track's code, in cashier words. A code this bundle does not know
 * shows as itself, so support can read it off the screen.
 */
export function notTakenReason(language: Language, code: string): string {
	const known: Record<string, MessageKey> = {
		TOTAL_MISMATCH: 'ordersNotTakenTotal',
		VALIDATION_ERROR: 'ordersNotTakenInvalid',
		IDEMPOTENCY_MISMATCH: 'ordersNotTakenHeld',
		ORDER_NUMBER_IN_USE: 'ordersNotTakenNumber',
		NOT_FOUND: 'ordersNotTakenOther',
	};
	const key = known[code];
	const reason = key ? message(language, key) : fill(message(language, 'ordersNotTakenCode'), code);
	return fill(message(language, 'ordersNotTakenTell'), reason);
}

/**
 * An order's payment: with the driver while it is open, void, free at a total
 * of 0, by card, or in cash with what was tendered and the change.
 */
export function payLine(language: Language, order: Order, minorDigits: number): string {
	if (order.status === 'open') return message(language, 'doneWithDriver');
	if (order.status === 'void') return message(language, 'ordersVoid');
	if (order.totalMinor === 0) return message(language, 'ordersFree');
	const payment = order.payment;
	if (!payment || payment.method === 'card') return message(language, 'ordersCard');
	return `${message(language, 'ordersCash')} ${formatMinor(payment.tenderedMinor, minorDigits)} · ${message(language, 'ordersChangeWord')} ${formatMinor(payment.changeMinor, minorDigits)}`;
}
